import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Box,
  Card,
  CardBody,
  IconButton,
  Image,
  Stack,
  Text,
} from "@chakra-ui/react";
import { faCommentAlt, faHeart } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useState, useCallback } from "react";
import ReadMore from "../../components/ReadMore";
import "./LandingInstagram.css";

interface InstagramPost {
  id: string;
  ig_id: string;
  media_type: "IMAGE" | "CAROUSEL_ALBUM" | "VIDEO";
  media_url: string;
  thumbnail_url: string;
  permalink: string;
  caption?: string;
  like_count?: number;
  comments_count?: number;
}

interface LandingInstagramProps {
  descriptions: Record<string, string | undefined>;
}

const LandingInstagram = ({ descriptions }: LandingInstagramProps) => {
  const [post, setPost] = useState<InstagramPost[]>([]);

  // Extract instagram_desc from the whole descriptions object
  const instagramDesc = descriptions?.instagram_desc;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: "start",
    loop: false,
  });

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/instagram-post`)
      .then((response) => response.json())
      .then((data) => setPost(data))
      .catch((err) => console.error("Error fetching Instagram posts:", err));
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section className="embla-ig">
      <Box py={10} textAlign="center">
        <Text fontSize="2xl" mb={2} fontWeight="bold">
          FOLLOW OUR INSTAGRAM
        </Text>

        {instagramDesc && (
          <Text
            fontSize="md"
            color="gray.600"
          >
            {instagramDesc}
          </Text>
        )}
      </Box>

      <Box px={{ base: 4, md: 12 }}>
        <div className="embla-inner">
          <IconButton
            aria-label="Previous"
            icon={<ArrowBackIcon />}
            onClick={scrollPrev}
            isDisabled={!canPrev}
            className="embla__button embla__button--prev"
          />

          <div className="embla__viewport-ig" ref={emblaRef}>
            <div className="embla__container-ig">
              {post.map((item) => (
                <div className="embla__slide-ig" key={item.id}>
                  <Card
                    maxW="sm"
                    p={2}
                    onClick={() => window.open(item.permalink)}
                    style={{ cursor: "pointer" }}
                  >
                    <CardBody style={{ padding: "0.5rem" }}>
                      {item.media_type !== "VIDEO" ? (
                        <Image
                          src={item.media_url}
                          alt={item.ig_id}
                          borderRadius="lg"
                        />
                      ) : (
                        <Box
                          position="relative"
                          cursor="pointer"
                          onClick={() => window.open(item.permalink)}
                        >
                          <Image
                            src={item.thumbnail_url}
                            alt={item.ig_id}
                            borderRadius="lg"
                            className="ig-image"
                          />
                          <Box
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            bg="rgba(0,0,0,0.4)"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="lg"
                          >
                            <Text fontSize="md" color="white" fontWeight="bold">
                              View Reels on Instagram
                            </Text>
                          </Box>
                        </Box>
                      )}

                      <Stack mt="3" spacing="2">
                        <Box>
                          <FontAwesomeIcon
                            icon={faHeart}
                            style={{ paddingRight: 5 }}
                          />
                          {item.like_count}
                          <FontAwesomeIcon
                            icon={faCommentAlt}
                            style={{ paddingRight: 5, paddingLeft: 8 }}
                          />
                          {item.comments_count}
                        </Box>
                        <ReadMore
                          text={item.caption ?? ""}
                          url={item.permalink}
                          wordLimit={15}
                        />
                      </Stack>
                    </CardBody>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          <IconButton
            aria-label="Next"
            icon={<ArrowForwardIcon />}
            onClick={scrollNext}
            isDisabled={!canNext}
            className="embla__button embla__button--next"
          />
        </div>
      </Box>
    </section>
  );
};

export default LandingInstagram;
